import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { LockKeyhole, ShieldCheck } from "lucide-react";
import { AppHeader } from "@/components/site/AppHeader";
import { JobPaymentCheckout } from "@/components/payments/JobPaymentCheckout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { getJobPayment, getJobPaymentAvailability } from "@/lib/job-payments.functions";
import { paymentsConfigured } from "@/lib/stripe";
import { gbp } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/bookings/$bookingId/payment")({
  head: () => ({ meta: [{ title: "Protected payment — Shields" }, { name: "robots", content: "noindex" }] }),
  component: ProtectedPaymentPage,
});

function ProtectedPaymentPage() {
  const { bookingId } = Route.useParams();
  const payment = useQuery({ queryKey: ["job-payment", bookingId], queryFn: () => getJobPayment({ data: { bookingId } }) });
  const availability = useQuery({ queryKey: ["job-payment-availability"], queryFn: () => getJobPaymentAvailability(), retry: false });
  const row = payment.data as any;
  const configured = paymentsConfigured();
  const alreadyProtected = row && !["payment_pending","payment_failed","payment_processing"].includes(String(row.status));

  return <div className="min-h-screen bg-background pb-24 text-foreground">
    <PaymentTestModeBanner />
    <AppHeader workspace="customer" />
    <main className="mx-auto w-full max-w-4xl px-4 py-6">
      <section className="border border-primary/30 bg-card/75 p-5 md:p-7">
        <p className="technical-label">PAYMENT &amp; BOOKING</p>
        <div className="mt-3 flex items-start gap-3"><span className="flex h-10 w-10 items-center justify-center border border-success/40 bg-success/10 text-success"><ShieldCheck className="h-5 w-5"/></span><div><h1 className="text-2xl font-black">{alreadyProtected ? paymentStatusTitle(String(row.status)) : "Protected payment for this job"}</h1><p className="mt-2 text-sm leading-6 text-muted-foreground">{alreadyProtected ? "The verified payment state is shown below. Partner release requires the authorised completion checks." : "Checkout appears only after protected payments are enabled for this job."}</p></div></div>
        {row && <div className="mt-6 grid grid-cols-3 gap-px border border-border bg-border text-center">
          <Amount label="AGREED JOB PRICE" value={row.agreed_price_pence}/>
          <Amount label="PROTECTION FEE" value={row.protection_fee_pence}/>
          <Amount label="CUSTOMER TOTAL" value={row.customer_total_pence} strong/>
        </div>}
      </section>
      <section className="mt-4">
        {payment.isLoading ? (
          <div className="border border-border bg-card p-5 text-sm text-muted-foreground">Checking payment status…</div>
        ) : payment.isError ? (
          <div className="border border-destructive/30 bg-destructive/5 p-5 text-sm">
            <strong>We couldn’t load the payment status.</strong>
            <p className="mt-2 text-muted-foreground">No payment has been simulated or marked successful.</p>
            <button type="button" onClick={() => payment.refetch()} className="btn-secondary-hero mt-4">Try again</button>
          </div>
        ) : alreadyProtected ? (
          <div className="border border-success/30 bg-success/5 p-5 text-sm">
            <ShieldCheck className="mb-3 h-5 w-5 text-success" />
            <strong>{paymentStatusTitle(String(row.status))}</strong>
            <p className="mt-2 text-muted-foreground">Shields will only change this state from verified payment and job events.</p>
          </div>
        ) : availability.isLoading ? (
          <div role="status" className="border border-border bg-card p-5 text-sm text-muted-foreground">Checking payment availability…</div>
        ) : availability.isError ? (
          <div className="border border-destructive/30 bg-destructive/5 p-5 text-sm"><strong>Payment availability could not be checked.</strong><button type="button" onClick={() => availability.refetch()} className="btn-secondary-hero mt-4">Try again</button></div>
        ) : !configured || !availability.data?.enabled ? (
          <div className="border border-warning/40 bg-warning/5 p-5 text-sm"><LockKeyhole className="mb-3 h-5 w-5 text-warning"/><strong>Protected payments are not enabled for this build.</strong><p className="mt-2 text-muted-foreground">Shields will not simulate a payment. Sign in with the verified Shields business account before Owner approval and sandbox certification.</p></div>
        ) : (
          <JobPaymentCheckout bookingId={bookingId}/>
        )}
      </section>
      <Link to="/bookings/$bookingId" params={{bookingId}} className="mt-5 inline-block text-xs font-bold text-muted-foreground">← Return to job</Link>
    </main>
  </div>;
}
function Amount({label,value,strong}:{label:string;value:number;strong?:boolean}){return <div className="bg-background/70 p-4"><span className="technical-label">{label}</span><span className={"mt-2 block tabular-nums "+(strong?"text-xl font-black text-primary":"text-base font-bold")}>{gbp(value)}</span></div>}

function paymentStatusTitle(status:string){
  if(status==="payment_protected") return "Payment protected";
  if(status==="payment_released") return "Payment released";
  if(status==="payment_refunded") return "Payment refunded";
  if(status==="payment_disputed") return "Payment under review";
  return "Payment already processed";
}
